import React, { useCallback } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { HeaderPage } from "../base/headerPage";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { setNewMessage } from "../../redux/actions/setting-actions";
import { LoadingPanel } from "src/components/loadingPanel";
import { ListBox } from "@progress/kendo-react-listbox";
import { ExpansionPanel, ExpansionPanelContent, ExpansionPanelActionEvent } from "@progress/kendo-react-layout";
import { Reveal } from "@progress/kendo-react-animation";
import { Input } from "@progress/kendo-react-inputs";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { filterBy } from "@progress/kendo-data-query";
import CustomDropdown from "src/components/customDropdown";
import ContractFields from "./ContractFields";
import ContractUploadFile from "./components/ContractUploadFile";
import moment from "moment";
import {
  dummyThresholdUnitTypeData,
  dummyRateUnitTypeData,
  dummyIsTransactionalData,
  dummyContractStructureData,
  dummyPeriodTypeData,
  dummyApproverData,
  dummyEmploymentTypeData,
} from "./dummyData";
import { getCountUnitContractValidationErrors } from "./utils/countUnitContractValidation";
import { formatUnitLabel } from "../../utils/payRequestUtils";
import { fetchApproverDetails } from "./services/approverDetailsService";
import { fetchLocationForProvider } from "./services/locationService";
import { fetchSpecialtyData } from "./services/specialtyService";
import { fetchContractTypeForProvider } from "./services/contractTypeService";
import { fetchDivisionData } from "./services/divisionService";
import { fetchTaskMasterData } from "./services/taskMasterService";
import { fetchNpiDataList, fetchPositionDataList, createNpiPositionMapping } from "./services/npiPositionService";
import { fetchContractForProvider } from "./services/contractDetailsService";
import { updateContract } from "./services/contractUpdateService";
import { fetchContractRateType } from "./services/rateTypeService";
import { fetchShiftType } from "./services/shiftTypeService";
import { getUserCredential } from "../../lib/cookies";
import { transformDateTime } from "src/lib/date-time.util";
import { DialogComponent } from "../../components/dialog";

const UpdateContract = ({ pageProps, editData, setMode, setIsUpdate, onUpdateContract }) => {
  const cookiesData = getUserCredential();
  const dispatch = useDispatch();
  const history = useHistory();

  const isLeeHealthTenant = cookiesData?.out_tenant_id === "leehealth001";
  const isCmnspiritTenant = cookiesData?.out_tenant_id === "cmnspiritwkd";
  const isCmnspiritCs03Tenant = cookiesData?.out_tenant_id === "cmnspirit003";

  const [loadingState, setLoadingState] = React.useState({
    initialLoad: true,
    fetchingContractData: false,
    submitting: false,
  });

  // State for API-fetched rate type data
  const [rateTypeApiData, setRateTypeApiData] = React.useState([]);
  const [isRateTypeLoading, setIsRateTypeLoading] = React.useState(false);

  // State for API-fetched shift type data
  const [shiftTypeData, setShiftTypeData] = React.useState([]);
  const [filteredShiftTypeData, setFilteredShiftTypeData] = React.useState([]);
  const [isShiftTypeLoading, setIsShiftTypeLoading] = React.useState(false);
  const [contractCategories, setContractCategories] = React.useState([]);

  // State for contract key and version (required for update)
  const [contractMstrKey, setContractMstrKey] = React.useState(editData?.contractMstrKey || null);
  const [version, setVersion] = React.useState(editData?.version || 1); // Default to 1 as backend requires version >= 1

  // State for expansion panel
  const [expandedApprover, setExpandedApprover] = React.useState("approver_0");
  const [expandedTier, setExpandedTier] = React.useState("tier_0");

  // State for upload popup
  const [openUploadPopup, setOpenUploadPopup] = React.useState(false);

  // State for NPI data fields
  const [npiDataFields, setNpiDataFields] = React.useState({
    npiData: [],
    npiDataValue: [],
    filterValue: "",
    search: "",
    isNpiDataLoading: false,
  });

  // State for Position Name data fields
  const [positionNameDataFields, setPositionNameDataFields] = React.useState({
    positionNameData: [],
    positionNameDataValue: [],
    filterValue: "",
    search: "",
    isPositionNameDataLoading: false,
  });

  // Store original NPI data for filter reset
  const [originalNpiData, setOriginalNpiData] = React.useState([]);

  // Store original position name data for filter reset
  const [originalPositionNameData, setOriginalPositionNameData] = React.useState([]);

  // State for Approver Name data fields
  const [approverDataFields, setApproverDataFields] = React.useState({
    approverData: [],
    approverDataValue: [],
    filterValue: "",
    search: "",
    isApproverDataLoading: false,
  });

  // Store original approver data for filter reset
  const [originalApproverData, setOriginalApproverData] = React.useState([]);

  // Store NPI/Position mapping data
  const [npiPositionRawData, setNpiPositionRawData] = React.useState([]);
  const [positionRawData, setPositionRawData] = React.useState([]);
  const [npiPositionMap, setNpiPositionMap] = React.useState({ npiToPosition: new Map(), positionToNpi: new Map() });

  // State for dropdown data
  const [dropdownData, setDropdownData] = React.useState({
    divisions: [],
    hospitals: [],
    specialties: [],
    contractTypes: [],
    contractNames: [],
  });

  // State for organizational attributes (flat properties)
  const [organizationalData, setOrganizationalData] = React.useState({
    division: "",
    hospital: "",
    department: "",
    specialty: "",
    facility: "",
    physicianNpi: "",
    positionName: "",
    employmentType: "",
    processLevel: "",
    providerMstrKey: null,
  });

  // State for contract data - simplified structure
  const [contractData, setContractData] = React.useState({
    contractType: "",
    contractName: "",
    contractStartDate: "",
    contractEndDate: "",
    shiftType: "",
    shiftTypeMstrKey: null,
    payCode: "",
    costCenter: "",
    companyCode: "",
    spendCategory: "",
    costingCompany: "",
    clmContractId: "",
    gracePeriod: 0,
    rateType: "",
    rateTypeMstrKey: null,
    isTransactional: "false",
    shift: 0,
    annualCap: 0,
    annualCompCap: 0,
    periodType: "",
    contractStructure: "",
    tiers: [
      {
        id: "tier_0",
        tierLevel: "",
        minimumThreshold: "",
        maximumThreshold: "",
        thresholdUnitType: "",
        rate: "",
        rateUnitType: "",
      },
    ],
    approvers: [
      {
        id: "approver_0",
        approverName: "",
        isRequired: "",
        approverLevel: "",
      },
    ],
    documents: [],
  });

  // State for validation errors
  const [validationErrors, setValidationErrors] = React.useState({});

  // State to store initial data for comparison
  const [initialOrganizationalData, setInitialOrganizationalData] = React.useState(null);
  const [initialContractData, setInitialContractData] = React.useState(null);

  // State to track if any field has been modified
  const [hasChanges, setHasChanges] = React.useState(false);

  // State for COMP_ADMIN approver toggle (will be set based on existing data)
  const [includeCompAdmin, setIncludeCompAdmin] = React.useState(false);

  // State for filtered dropdown data
  const [filteredEmploymentTypeData, setFilteredEmploymentTypeData] = React.useState(dummyEmploymentTypeData);
  const [filteredIsTransactionalData, setFilteredIsTransactionalData] = React.useState(dummyIsTransactionalData);
  const [filteredPeriodTypeData, setFilteredPeriodTypeData] = React.useState(dummyPeriodTypeData);
  const [filteredRateTypeData, setFilteredRateTypeData] = React.useState([]);
  const [filteredThresholdUnitTypeData, setFilteredThresholdUnitTypeData] = React.useState({});
  const [filteredRateUnitTypeData, setFilteredRateUnitTypeData] = React.useState({});
  const [filteredTierLevelData, setFilteredTierLevelData] = React.useState({});
  const [filteredApproverLevelData, setFilteredApproverLevelData] = React.useState({});
  const [filteredIsRequiredData, setFilteredIsRequiredData] = React.useState({});

  /**
   * Field Disabled Check
   *
   * All fields are enabled regardless of other field values.
   * All dropdown options are available in all dropdowns regardless of selections.
   *
   * @param {string} fieldName - The name of the field to check
   * @returns {boolean} - Always returns false (all fields enabled)
   */
  const isFieldDisabled = (fieldName) => {
    return false; // All fields enabled
  };

  const initialContractCategories = [
    {
      category_code: "org_attr",
      category_label: "ORGANIZATIONAL ATTRIBUTES",
      fields: [
        { filter_label: "Division", filter_column_name: "division", filter_column_datatype: "string", is_nullable: 0 },
        {
          filter_label: "Hospital / Location",
          filter_column_name: "hospital",
          filter_column_datatype: "string",
          is_nullable: 0,
        },
        {
          filter_label: "Department",
          filter_column_name: "department",
          filter_column_datatype: "string",
          is_nullable: 1,
        },
        {
          filter_label: "Specialty",
          filter_column_name: "specialty",
          filter_column_datatype: "string",
          is_nullable: 0,
        },
        { filter_label: "Facility", filter_column_name: "facility", filter_column_datatype: "string", is_nullable: 1 },
        {
          filter_label: "Employee NPI",
          filter_column_name: "physicianNpi",
          filter_column_datatype: "string",
          is_nullable: 1,
        },
        {
          filter_label: "Position Name",
          filter_column_name: "positionName",
          filter_column_datatype: "string",
          is_nullable: 1,
        },
        {
          filter_label: "Employment Type",
          filter_column_name: "employmentType",
          filter_column_datatype: "string",
          is_nullable: 0,
        },
      ],
    },
    {
      category_code: "contract_attr",
      category_label: "CONTRACT ATTRIBUTES",
      fields: [
        {
          filter_label: "Contract Type",
          filter_column_name: "contractType",
          filter_column_datatype: "string",
          is_nullable: 0,
        },
        {
          filter_label: "Contract Name",
          filter_column_name: "contractName",
          filter_column_datatype: "string",
          is_nullable: 0,
        },
        {
          filter_label: "Contract Start Date",
          filter_column_name: "contractStartDate",
          filter_column_datatype: "date",
          is_nullable: 0,
        },
        {
          filter_label: "Contract End Date",
          filter_column_name: "contractEndDate",
          filter_column_datatype: "date",
          is_nullable: 0,
        },
        {
          filter_label: "Contract Structure",
          filter_column_name: "contractStructure",
          filter_column_datatype: "string",
          is_nullable: 0,
        },
        {
          filter_label: "Minimum Threshold",
          filter_column_name: "minimumThreshold",
          filter_column_datatype: "number",
          is_nullable: 1,
        },
        {
          filter_label: "Maximum Threshold",
          filter_column_name: "maximumThreshold",
          filter_column_datatype: "number",
          is_nullable: 1,
        },
        {
          filter_label: "Threshold Unit Type",
          filter_column_name: "thresholdUnitType",
          filter_column_datatype: "string",
          is_nullable: 1,
        },
        { filter_label: "Rate", filter_column_name: "rate", filter_column_datatype: "string", is_nullable: 0 },
        { filter_label: "Rate Type", filter_column_name: "rateType", filter_column_datatype: "string", is_nullable: 0 },
        {
          filter_label: "Rate Unit Type",
          filter_column_name: "rateUnitType",
          filter_column_datatype: "string",
          is_nullable: 0,
        },
        {
          filter_label: "Is Transactional",
          filter_column_name: "isTransactional",
          filter_column_datatype: "string",
          is_nullable: 1,
        },
        { filter_label: "Shift", filter_column_name: "shift", filter_column_datatype: "string", is_nullable: 1 },
        {
          filter_label: "Annual Cap",
          filter_column_name: "annualCap",
          filter_column_datatype: "string",
          is_nullable: 1,
        },
        {
          filter_label: "Annual Comp Cap",
          filter_column_name: "annualCompCap",
          filter_column_datatype: "string",
          is_nullable: 1,
        },
        {
          filter_label: "Period Type",
          filter_column_name: "periodType",
          filter_column_datatype: "string",
          is_nullable: 0,
        },
      ],
    },
  ];

  // Initialize contract categories with unique keys for dragging
  // Also applies tenant-specific field requirements (is_nullable)
  React.useEffect(() => {
    const categoriesWithKeys = initialContractCategories.map((category, index) => ({
      ...category,
      category_config_key: index + 1,
      fields: category.fields.map((field, fieldIndex) => {
        let isNullable = field.is_nullable;

        if (isLeeHealthTenant) {
          if (field.filter_column_name === "division" || field.filter_column_name === "employmentType") {
            isNullable = 1;
          }
        }

        return {
          ...field,
          is_nullable: isNullable,
          cat_fdm_assign_key: (index + 1) * 1000 + fieldIndex + 1,
        };
      }),
    }));
    setContractCategories(categoriesWithKeys);
  }, [isLeeHealthTenant]);

  // Fetch all dropdown data on component mount
  const fetchAllDropdownData = async () => {
    try {
      // Fetch all dropdown data in parallel
      const [divisionResult, hospitalResult, specialtyResult, contractNameResult] = await Promise.all([
        fetchDivisionData(cookiesData, dispatch),
        fetchLocationForProvider(cookiesData, null, dispatch),
        fetchSpecialtyData(cookiesData, dispatch),
        fetchTaskMasterData(cookiesData, dispatch),
      ]);

      // Extract data from results
      const divisions = divisionResult.success ? divisionResult.transformedData : [];
      const hospitals = hospitalResult.success ? hospitalResult.transformedData : [];
      const specialties = specialtyResult.success ? specialtyResult.specialtyData : [];
      const contractNames = contractNameResult.success ? contractNameResult.transformedData : [];

      // Fetch contract types (requires hospitals data)
      let contractTypes = [];
      if (hospitals.length > 0) {
        const contractTypeResult = await fetchContractTypeForProvider(cookiesData, null, hospitals, dispatch);
        contractTypes = contractTypeResult.success ? contractTypeResult.contractTypesForFilter : [];
      }

      // Update all dropdown data at once
      setDropdownData({
        divisions,
        hospitals,
        specialties,
        contractTypes,
        contractNames,
      });
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      setDropdownData({
        divisions: [],
        hospitals: [],
        specialties: [],
        contractTypes: [],
        contractNames: [],
      });
    }
  };

  // Functions to add new tiers
  const addNewTier = () => {
    setContractData((prev) => {
      if (prev.tiers.length >= 5) {
        return prev;
      }
      const newTierId = `tier_${prev.tiers.length}`;
      return {
        ...prev,
        tiers: [
          ...prev.tiers,
          {
            id: newTierId,
            tierLevel: "",
            minimumThreshold: "",
            maximumThreshold: "",
            thresholdUnitType: "",
            rate: "",
            rateUnitType: "",
          },
        ],
      };
    });
    // Automatically expand the newly added tier
    setExpandedTier(`tier_${contractData.tiers.length}`);
  };

  // Functions to remove tiers
  const removeTier = (index) => {
    if (contractData.tiers.length > 1) {
      const tierToRemove = contractData.tiers[index];
      setContractData((prev) => ({
        ...prev,
        tiers: prev.tiers.filter((_, i) => i !== index),
      }));

      // If the removed tier was expanded, collapse the panel
      if (expandedTier === tierToRemove.id) {
        setExpandedTier("tier_0");
      }
    }
  };

  // Functions to add new approvers
  const addNewApprover = () => {
    const newApproverId = `approver_${contractData.approvers.length}`;
    setContractData((prev) => ({
      ...prev,
      approvers: [
        ...prev.approvers,
        {
          id: newApproverId,
          approverName: "",
          isRequired: "",
          approverLevel: "",
        },
      ],
    }));
    // Automatically expand the newly added approver
    setExpandedApprover(newApproverId);
  };

  // Functions to remove approvers
  const removeApprover = (index) => {
    const approverToRemove = contractData.approvers[index];
    setContractData((prev) => ({
      ...prev,
      approvers: prev.approvers.filter((_, i) => i !== index),
    }));

    // If the removed approver was expanded, collapse the panel
    if (expandedApprover === approverToRemove.id) {
      // Set to the first remaining approver or 'approver_0' if none remain
      const remainingApprovers = contractData.approvers.filter((_, i) => i !== index);
      if (remainingApprovers.length > 0) {
        setExpandedApprover(remainingApprovers[0].id);
      } else {
        setExpandedApprover("approver_0");
      }
    }
  };

  // Filter change handler for MultiSelect
  const filterChange = (event, setDataFields, moduleData, key) => {
    let dataValue = filterBy(moduleData.slice(), event.filter);
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
      filterValue: event.filter.value,
      search: event.filter,
    }));
  };

  // Open change handler for MultiSelect
  const onOpenChange = (setDataFields, moduleData, key, dataFields, dataFieldsValue) => {
    let dataValue =
      dataFields.filterValue && dataFields.filterValue !== ""
        ? filterBy(dataFieldsValue.slice(), dataFields.search)
        : moduleData;
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
    }));
  };

  // Auto-select positions when NPI is selected
  // Commented out - Employee NPI field is no longer required
  /* const handleNpiChange = (selectedNpis) => {
        // Limit to single selection
        let finalSelection = selectedNpis;
        if (selectedNpis.length > 1) {
            finalSelection = [selectedNpis[selectedNpis.length - 1]];
        }

        // Update NPI field
        setNpiDataFields(prev => ({
            ...prev,
            npiDataValue: finalSelection
        }));

        // Update organizational data with the selected NPI text
        if (finalSelection.length > 0) {
            // Get selected NPI value
            const selectedNpiValue = finalSelection[0].id;
            
            // Find the provider_mstr_key from raw data based on NPI
            const selectedItem = npiPositionRawData.find(item => item.npi === selectedNpiValue);
            const providerKey = selectedItem?.provider_mstr_key || null;

            setOrganizationalData(prev => ({
                ...prev,
                physicianNpi: finalSelection[0].text,
                providerMstrKey: providerKey
            }));
            
            // Clear validation error for providerMstrKey
            if (validationErrors['providerMstrKey']) {
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors['providerMstrKey'];
                    return newErrors;
                });
            }

            // Find corresponding position key
            const positionKey = npiPositionMap.npiToPosition.get(selectedNpiValue);
            if (positionKey) {
                const positionToSelect = originalPositionNameData.find(pos => pos.id === positionKey);
                if (positionToSelect) {
                    setPositionNameDataFields(prev => ({
                        ...prev,
                        positionNameDataValue: [positionToSelect]
                    }));
                    setOrganizationalData(prev => ({
                        ...prev,
                        positionName: positionToSelect.text
                    }));
                    
                    // Clear validation error for positionName
                    if (validationErrors['positionName']) {
                        setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors['positionName'];
                            return newErrors;
                        });
                    }
                }
            }
        } else {
            // Clear NPI and Position
            setOrganizationalData(prev => ({
                ...prev,
                physicianNpi: "",
                positionName: "",
                providerMstrKey: null
            }));
            setPositionNameDataFields(prev => ({
                ...prev,
                positionNameDataValue: []
            }));
        }
    }; */

  // Auto-select NPIs when position is selected
  const handlePositionChange = (selectedPositions) => {
    // Limit to single selection
    let finalSelection = selectedPositions;
    if (selectedPositions.length > 1) {
      finalSelection = [selectedPositions[selectedPositions.length - 1]];
    }
    // Update Position field
    setPositionNameDataFields((prev) => ({
      ...prev,
      positionNameDataValue: finalSelection,
    }));

    // Update organizational data with the selected position text
    if (finalSelection.length > 0) {
      // Get selected position key
      const selectedPositionKey = finalSelection[0].id;

      // Find the provider_mstr_key from raw data based on position
      const selectedItem = positionRawData.find((item) => item.org_position_key === selectedPositionKey);
      const providerKey = selectedItem?.provider_mstr_key || null;

      setOrganizationalData((prev) => ({
        ...prev,
        positionName: finalSelection[0].text,
        providerMstrKey: providerKey,
      }));

      // Clear validation errors for providerMstrKey and positionName
      if (validationErrors["providerMstrKey"]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors["providerMstrKey"];
          return newErrors;
        });
      }
      if (validationErrors["positionName"]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors["positionName"];
          return newErrors;
        });
      }

      // Find corresponding NPI value
      // Commented out - Employee NPI mapping is no longer required
      /* const npiValue = npiPositionMap.positionToNpi.get(selectedPositionKey);
            if (npiValue !== undefined) {
                const npiToSelect = originalNpiData.find(npi => npi.id === npiValue);
                if (npiToSelect) {
                    setNpiDataFields(prev => ({
                        ...prev,
                        npiDataValue: [npiToSelect]
                    }));
                    setOrganizationalData(prev => ({
                        ...prev,
                        physicianNpi: npiToSelect.text
                    }));
                }
            } */
    } else {
      // Clear Position and NPI
      setOrganizationalData((prev) => ({
        ...prev,
        positionName: "",
        // physicianNpi: "", // Commented out - NPI no longer required
        providerMstrKey: null,
      }));
      // setNpiDataFields(prev => ({ // Commented out - NPI no longer required
      //     ...prev,
      //     npiDataValue: []
      // }));
    }
  };

  // Handle approver selection change
  const handleApproverChange = (index, selectedApprovers) => {
    // Limit to single selection
    let finalSelection = selectedApprovers;
    if (selectedApprovers.length > 1) {
      finalSelection = [selectedApprovers[selectedApprovers.length - 1]];
    }

    // Update approver data in contractData with the selected approver name
    if (finalSelection.length > 0) {
      updateApproverData(index, "approverName", finalSelection[0].text);
    } else {
      updateApproverData(index, "approverName", "");
    }
    // Error clearing is handled in updateApproverData
  };

  // Update organizational data
  const updateOrganizationalData = (field, value) => {
    // Handle dropdown values (extract text if it's an object)
    const actualValue = typeof value === "object" && value !== null && value.text ? value.text : value;

    setOrganizationalData((prev) => ({
      ...prev,
      [field]: actualValue,
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Helper function to check if rate type is step/cliff/multisite (non-flat types)
  const isNonFlatRateType = (rateType) => {
    if (!rateType) return false;
    const normalizedRateType = rateType.toLowerCase();
    return normalizedRateType === "step" || normalizedRateType === "cliff" || normalizedRateType === "multisite" || normalizedRateType === "multisite-day" || normalizedRateType === "multisite-periodend" || normalizedRateType === "stemi" || normalizedRateType === "gen-cardio";
  };

  // Validate rate type change
  const validateRateTypeChange = (currentRateType, newRateType, currentTierCount) => {
    // Normalize rate types for comparison
    const normalizedCurrent = currentRateType ? currentRateType.toLowerCase() : "";
    const normalizedNew = newRateType ? newRateType.toLowerCase() : "";

    // Check if trying to change from non-flat (step/cliff/multisite) to flat with multiple tiers
    if (isNonFlatRateType(normalizedCurrent) && normalizedNew === "flat" && currentTierCount > 1) {
      return {
        isValid: false,
        message: "There should be only 1 tier level to go to flat",
      };
    }

    return { isValid: true };
  };

  // Update contract data
  const updateContractData = (field, value) => {
    // Handle dropdown values (extract text if it's an object)
    const actualValue = typeof value === "object" && value !== null && value.text ? value.text : value;

    setContractData((prev) => ({
      ...prev,
      [field]: actualValue,
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // If updating a date field, also clear the other date field's error and revalidate date logic
    if (field === "contractStartDate" || field === "contractEndDate") {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors["contractStartDate"];
        delete newErrors["contractEndDate"];
        return newErrors;
      });

      // Validate required dates
      const updatedData = { ...contractData, [field]: actualValue };
      const startDateValue = updatedData.contractStartDate;
      const endDateValue = updatedData.contractEndDate;
      if (
        !startDateValue ||
        (typeof startDateValue === "string" && startDateValue.trim() === "") ||
        (startDateValue instanceof Date && isNaN(startDateValue.getTime()))
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          contractStartDate: "Contract Start Date is required",
        }));
      }
      if (
        !endDateValue ||
        (typeof endDateValue === "string" && endDateValue.trim() === "") ||
        (endDateValue instanceof Date && isNaN(endDateValue.getTime()))
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          contractEndDate: "Contract End Date is required",
        }));
      }

      // Revalidate date comparison if both dates are present
      if (updatedData.contractStartDate && updatedData.contractEndDate) {
        const startDate = new Date(updatedData.contractStartDate);
        const endDate = new Date(updatedData.contractEndDate);

        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);

          if (startDate > endDate) {
            setValidationErrors((prev) => ({
              ...prev,
              contractStartDate: "Contract Start Date cannot be after Contract End Date",
              contractEndDate: "Contract End Date cannot be before Contract Start Date",
            }));
          }
        }
      }
    }

    // Validate shift field if it's being updated
    if (field === "shift") {
      const shiftValue =
        actualValue === "" || actualValue === null || actualValue === undefined ? null : parseFloat(actualValue);

      // Only set error if value is invalid (error is already cleared at the start of function if field had previous error)
      if (shiftValue !== null && !isNaN(shiftValue) && (shiftValue < 0 || shiftValue > 24)) {
        setValidationErrors((prev) => ({
          ...prev,
          shift: "Shift hours must be between 0 and 24",
        }));
      }
    }

    // Validate grace period field if it's being updated
    if (field === "gracePeriod") {
      const gracePeriodValue =
        actualValue === "" || actualValue === null || actualValue === undefined ? null : Number(actualValue);

      if (gracePeriodValue !== null && !isNaN(gracePeriodValue)) {
        if (!Number.isInteger(gracePeriodValue)) {
          setValidationErrors((prev) => ({ ...prev, gracePeriod: "Grace period must be a whole number" }));
        } else if (gracePeriodValue < 0) {
          setValidationErrors((prev) => ({ ...prev, gracePeriod: "Grace period cannot be negative" }));
        }
      }
    }
  };

  // Refresh dropdown data after adding new options
  const handleRefreshDropdownData = async (fieldName, newlyAddedItem = null) => {
    const cookiesData = getUserCredential();

    try {
      let refreshedData = null;

      switch (fieldName) {
        case "specialty":
          const specialtyResult = await fetchSpecialtyData(cookiesData, dispatch);
          if (specialtyResult.success) {
            refreshedData = specialtyResult.specialtyData;
            setDropdownData((prev) => ({
              ...prev,
              specialties: refreshedData,
            }));
          }
          break;

        case "division":
          const divisionResult = await fetchDivisionData(cookiesData, dispatch);
          if (divisionResult.success) {
            refreshedData = divisionResult.transformedData;
            setDropdownData((prev) => ({
              ...prev,
              divisions: refreshedData,
            }));
          }
          break;

        case "hospital":
          const hospitalResult = await fetchLocationForProvider(cookiesData, null, dispatch);
          if (hospitalResult.success) {
            refreshedData = hospitalResult.transformedData;
            setDropdownData((prev) => ({
              ...prev,
              hospitals: refreshedData,
            }));
          }
          break;

        case "contractType":
          const currentHospitals = dropdownData.hospitals;
          if (currentHospitals.length > 0) {
            const contractTypeResult = await fetchContractTypeForProvider(
              cookiesData,
              null,
              currentHospitals,
              dispatch
            );
            if (contractTypeResult.success) {
              refreshedData = contractTypeResult.contractTypesForFilter;
              setDropdownData((prev) => ({
                ...prev,
                contractTypes: refreshedData,
              }));
            }
          }
          break;

        case "contractName":
          const contractNameResult = await fetchTaskMasterData(cookiesData, dispatch);
          if (contractNameResult.success) {
            refreshedData = contractNameResult.transformedData;
            setDropdownData((prev) => ({
              ...prev,
              contractNames: refreshedData,
            }));
          }
          break;

        default:
          console.warn(`No refresh handler for field: ${fieldName}`);
      }

      // Auto-select the newly added item if provided
      if (newlyAddedItem && refreshedData && Array.isArray(refreshedData)) {
        // Find the newly added item in the refreshed data by matching text
        const foundItem = refreshedData.find((item) => item.text === newlyAddedItem.text);

        if (foundItem) {
          // Determine which state update function to use based on field name
          if (["division", "hospital", "specialty"].includes(fieldName)) {
            updateOrganizationalData(fieldName, foundItem);
          } else if (["contractType", "contractName"].includes(fieldName)) {
            // Map contractType to contractType field name
            const contractFieldName = fieldName === "contractType" ? "contractType" : "contractName";
            updateContractData(contractFieldName, foundItem);
          }
        }
      }
    } catch (error) {
      console.error(`Error refreshing ${fieldName} data:`, error);
    }
  };

  // Update tier data
  const updateTierData = (index, field, value) => {
    setContractData((prev) => {
      const updatedTiers = [...prev.tiers];
      updatedTiers[index] = {
        ...updatedTiers[index],
        [field]: value,
      };

      // If rate type is being updated, also update contract structure with the same value
      if (field === "rateType") {
        return {
          ...prev,
          contractStructure: value,
          tiers: updatedTiers,
        };
      }

      return {
        ...prev,
        tiers: updatedTiers,
      };
    });

    // If tierLevel is being updated, clear filtered state for all tier dropdowns
    // This ensures all dropdowns recalculate their available options
    if (field === "tierLevel") {
      setFilteredTierLevelData({});
    }

    // Clear validation error for this tier field
    const errorKey = `tier_${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Update approver data
  const updateApproverData = (index, field, value) => {
    setContractData((prev) => {
      const updatedApprovers = [...prev.approvers];
      updatedApprovers[index] = {
        ...updatedApprovers[index],
        [field]: value,
      };
      return {
        ...prev,
        approvers: updatedApprovers,
      };
    });

    // If approverLevel is being updated, clear filtered state for all approver dropdowns
    // This ensures all dropdowns recalculate their available options
    if (field === "approverLevel") {
      setFilteredApproverLevelData({});
    }

    // Clear validation error for this approver field
    const errorKey = `approver_${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Handle document upload
  const handleDocumentUpload = (files) => {
    setContractData((prev) => ({
      ...prev,
      documents: [...prev.documents, ...Array.from(files)],
    }));
  };

  // Handle document updates from ContractUploadFile component
  const handleDocumentsUpdate = (uploadedFiles) => {
    setContractData((prev) => ({
      ...prev,
      documents: [...prev.documents, ...uploadedFiles],
    }));
  };

  // Remove document
  const removeDocument = (index) => {
    setContractData((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));
  };

  // Form data collection
  const collectFormData = () => {
    // Helper function to find ID by text in dropdown data
    const findIdByText = (dataArray, textValue) => {
      const item = dataArray?.find((item) => item.text === textValue);
      return item?.id || null;
    };

    // Transform tiers using selected Tier Level integer and rename fields
    // NOTE: Rate is sent as-is to the backend. Users enter the actual rate value directly.
    const transformedTiers = contractData.tiers.map((tier) => {
      const rateUnitType = tier.rateUnitType.toLowerCase() || "";
      let rate = tier.rate !== undefined && tier.rate !== null ? parseFloat(tier.rate) : null;

      return {
        tier: (() => {
          const m = tier.tierLevel?.match(/\d+/);
          return m ? parseInt(m[0]) : null;
        })(),
        rate: rate,
        rate_unit_type: rateUnitType,
        min_threshold: tier.minimumThreshold ? parseFloat(tier.minimumThreshold) : null,
        max_threshold: tier.maximumThreshold ? parseFloat(tier.maximumThreshold) : null,
        threshold_unit_type: tier.thresholdUnitType.toLowerCase() || "",
      };
    });

    // Group approvers by level (sort_order) with ID arrays
    const approversByLevel = {};
    contractData.approvers.forEach((approver) => {
      // Extract numeric value from "Level 1", "Level 2" etc.
      const levelMatch = approver.approverLevel?.match(/\d+/);
      const level = levelMatch ? parseInt(levelMatch[0]) : 1;

      if (!approversByLevel[level]) {
        approversByLevel[level] = {
          sort_order: level,
          is_required: approver.isRequired === "Yes" || approver.isRequired === true ? 1 : 0,
          approvers: [],
        };
      }

      // Map approver name to ID
      if (approver.approverName) {
        const approverId = findIdByText(originalApproverData, approver.approverName);
        if (approverId !== null) {
          approversByLevel[level].approvers.push(approverId);
        }
      }
    });

    const transformedApprovalLevels = Object.values(approversByLevel);

    // Add COMP_ADMIN approver level only if checkbox is checked
    if (includeCompAdmin) {
      transformedApprovalLevels.push({
        sort_order: contractData.approvers.length + 1,
        is_required: 0,
        approvers: ["COMP_ADMIN"],
      });
    }

    // Helper function to normalize period type for backend
    const normalizePeriodType = (periodType) => {
      if (!periodType) return "monthly";
      return periodType.toLowerCase();
    };

    // Build API request format
    const apiFormData = {
      in_user_id: cookiesData?.out_user_id || null,
      in_tenant_id: cookiesData?.out_tenant_id || "",
      in_contract_mstr_key: contractMstrKey || null,
      in_version: version || 1, // Version is required (min: 1), default to 1 if not set
      in_provider_id: organizationalData.providerMstrKey || null,
      in_location_mstr_key: findIdByText(dropdownData.hospitals, organizationalData.hospital),
      in_contract_name_mstr_key: findIdByText(dropdownData.contractNames, contractData.contractName),
      in_contract_type_mstr_key: findIdByText(dropdownData.contractTypes, contractData.contractType),
      in_specialty_mstr_key: findIdByText(dropdownData.specialties, organizationalData.specialty),
      in_annual_cap: parseInt(contractData.annualCap),
      in_annual_comp_cap: parseInt(contractData.annualCompCap),
      in_shift: parseInt(contractData.shift),
      in_period_type: normalizePeriodType(contractData.periodType),
      in_rate_type_mstr_key: contractData.rateTypeMstrKey || null,
      in_is_transactional: (() => {
        const value = contractData.isTransactional;
        // Convert to integer: 1 if truthy/yes/1, otherwise 0
        if (value === "Yes" || value === true || value === "1" || value === 1 || value === "true") {
          return 1;
        }
        return 0;
      })(),
      in_contract_start_date: contractData.contractStartDate
        ? `${transformDateTime({ date: contractData.contractStartDate, format: "YYYY-MM-DD" })} 00:00:00`
        : null,
      in_contract_end_date: contractData.contractEndDate
        ? `${transformDateTime({ date: contractData.contractEndDate, format: "YYYY-MM-DD" })} 23:59:59`
        : null,
      in_pay_code: contractData.payCode || null,
      in_cost_center: contractData.costCenter || null,
      in_company_code: parseInt(contractData.companyCode),
      in_spend_category: contractData.spendCategory || null,
      in_costing_company: contractData.costingCompany || null,
      in_clm_contract_id: contractData.clmContractId || null,
      in_grace_period_days: contractData.gracePeriod !== "" && contractData.gracePeriod !== null && contractData.gracePeriod !== undefined ? parseInt(contractData.gracePeriod) : null,
      in_division_mstr_key: findIdByText(dropdownData.divisions, organizationalData.division),
      in_employment_type: organizationalData.employmentType || null,
      in_shift_type_mstr_key: contractData.shiftTypeMstrKey || null,
      in_process_level: isCmnspiritCs03Tenant ? organizationalData.processLevel || null : null,
      in_department: isCmnspiritCs03Tenant ? organizationalData.department || null : null,
      in_tiers: transformedTiers.length > 0 ? transformedTiers : null,
      in_approval_levels: transformedApprovalLevels.length > 0 ? transformedApprovalLevels : null,
    };

    return apiFormData;
  };

  // Validate all required fields
  const validateRequiredFields = () => {
    const errors = {};

    // Validate organizational attributes
    if (!isLeeHealthTenant) {
      if (!organizationalData.division || organizationalData.division.trim() === "") {
        errors["division"] = "Division is required";
      }
    }
    if (!organizationalData.hospital || organizationalData.hospital.trim() === "") {
      errors["hospital"] = "Hospital / Location is required";
    }
    if (!organizationalData.specialty || organizationalData.specialty.trim() === "") {
      errors["specialty"] = "Specialty is required";
    }
    // Commented out - Employee NPI validation is no longer required
    // Position Name selection will provide provider_id
    // if (!organizationalData.providerMstrKey || organizationalData.providerMstrKey === null) {
    //     errors['providerMstrKey'] = 'Employee NPI is required';
    // }
    // if (!organizationalData.positionName || organizationalData.positionName.trim() === '') {
    //     errors['positionName'] = 'Position Name is required';
    // } else {
    //     // Validate that provider_id is set (from Position Name selection)
    //     if (!organizationalData.providerMstrKey || organizationalData.providerMstrKey === null) {
    //         errors['positionName'] = 'Position Name is required';
    //     }
    // }
    if (!isLeeHealthTenant) {
      if (!organizationalData.employmentType || organizationalData.employmentType.trim() === "") {
        errors["employmentType"] = "Employment Type is required";
      }
    }

    // Validate contract attributes
    if (!contractData.contractType || contractData.contractType.trim() === "") {
      errors["contractType"] = "Contract Type is required";
    }
    if (!contractData.contractName || contractData.contractName.trim() === "") {
      errors["contractName"] = "Contract Name is required";
    }
    if (!contractData.rateType || contractData.rateType.trim() === "") {
      errors["rateType"] = "Rate Type is required";
    }
    if (!contractData.shiftType || contractData.shiftType.trim() === "") {
      errors["shiftType"] = "Shift Type is required";
    }
    if (!contractData.periodType || contractData.periodType.trim() === "") {
      errors["periodType"] = "Period Type is required";
    }

    // Validate contract dates - required fields
    const startDateValue = contractData.contractStartDate;
    const endDateValue = contractData.contractEndDate;
    if (
      !startDateValue ||
      (typeof startDateValue === "string" && startDateValue.trim() === "") ||
      (startDateValue instanceof Date && isNaN(startDateValue.getTime()))
    ) {
      errors["contractStartDate"] = "Contract Start Date is required";
    }
    if (
      !endDateValue ||
      (typeof endDateValue === "string" && endDateValue.trim() === "") ||
      (endDateValue instanceof Date && isNaN(endDateValue.getTime()))
    ) {
      errors["contractEndDate"] = "Contract End Date is required";
    }

    // Validate contract dates - start date should not be after end date
    if (contractData.contractStartDate && contractData.contractEndDate) {
      const startDate = new Date(contractData.contractStartDate);
      const endDate = new Date(contractData.contractEndDate);

      // Check if dates are valid
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        // Compare dates (set time to midnight for accurate comparison)
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        if (startDate > endDate) {
          errors["contractStartDate"] = "Contract Start Date cannot be after Contract End Date";
          errors["contractEndDate"] = "Contract End Date cannot be before Contract Start Date";
        }
      }
    }

    // Validate tier fields - only required when contract structure is NOT "Flat"
    const isFlat = contractData.contractStructure === "Flat" || contractData.rateType === "Flat";
    if (!isFlat && contractData.tiers && contractData.tiers.length > 0) {
      contractData.tiers.forEach((tier, index) => {
        if (!tier.tierLevel || tier.tierLevel.trim() === "") {
          errors[`tier_${index}_tierLevel`] = "Tier Level is required";
        }
        if (!tier.rateUnitType || tier.rateUnitType.trim() === "") {
          errors[`tier_${index}_rateUnitType`] = "Rate Unit Type is required";
        }
        if (!tier.thresholdUnitType || tier.thresholdUnitType.trim() === "") {
          errors[`tier_${index}_thresholdUnitType`] = "Threshold Unit Type is required";
        }
      });
    }

    Object.assign(
      errors,
      getCountUnitContractValidationErrors(contractData.tiers || [], contractData.rateType)
    );

    // Validate approver fields
    if (contractData.approvers && contractData.approvers.length > 0) {
      contractData.approvers.forEach((approver, index) => {
        if (!approver.approverLevel || approver.approverLevel.trim() === "") {
          errors[`approver_${index}_approverLevel`] = "Approver Level is required";
        }
        if (!approver.approverName || approver.approverName.trim() === "") {
          errors[`approver_${index}_approverName`] = "Approver Name is required";
        }
        if (!approver.isRequired || approver.isRequired.trim() === "") {
          errors[`approver_${index}_isRequired`] = "Is Required is required";
        }
      });
    }

    // Validate cmnspiritwkd accounting fields
    if (isCmnspiritTenant) {
      if (!contractData.payCode || contractData.payCode.trim() === "") {
        errors["payCode"] = "Pay Code is required";
      }
      if (!contractData.costCenter || contractData.costCenter.trim() === "") {
        errors["costCenter"] = "Cost Center is required";
      }
      if (!contractData.clmContractId || contractData.clmContractId.trim() === "") {
        errors["clmContractId"] = "Contract ID is required";
      }
      if (
        contractData.companyCode === null ||
        contractData.companyCode === undefined ||
        String(contractData.companyCode).trim() === ""
      ) {
        errors["companyCode"] = "Company Code is required";
      }
    }

    // Validate grace period - must be a non-negative whole number
    const gracePeriodValue = contractData.gracePeriod !== "" && contractData.gracePeriod !== null && contractData.gracePeriod !== undefined
      ? Number(contractData.gracePeriod)
      : null;
    if (gracePeriodValue !== null && !isNaN(gracePeriodValue)) {
      if (!Number.isInteger(gracePeriodValue)) {
        errors["gracePeriod"] = "Grace period must be a whole number";
      } else if (gracePeriodValue < 0) {
        errors["gracePeriod"] = "Grace period cannot be negative";
      }
    }

    return errors;
  };

  // Fetch NPI data
  const handleFetchNpiData = async () => {
    try {
      setNpiDataFields((prev) => ({ ...prev, isNpiDataLoading: true }));

      const result = await fetchNpiDataList(null, null, cookiesData, dispatch, history, true);

      setOriginalNpiData(result.transformedData);
      setNpiDataFields((prev) => ({
        ...prev,
        npiData: result.transformedData,
        isNpiDataLoading: false,
      }));
      // Store raw data for mapping
      setNpiPositionRawData(result.rawData);

      // NPI will be populated after contract data is fetched
    } catch (error) {
      console.error("Error fetching NPI data:", error);
      setOriginalNpiData([]);
      setNpiDataFields((prev) => ({
        ...prev,
        npiData: [],
        isNpiDataLoading: false,
      }));
    }
  };

  // Fetch position data
  const handleFetchPositionData = async () => {
    try {
      setPositionNameDataFields((prev) => ({ ...prev, isPositionNameDataLoading: true }));

      const result = await fetchPositionDataList(null, null, cookiesData, dispatch, history, true);

      setOriginalPositionNameData(result.transformedData);
      setPositionNameDataFields((prev) => ({
        ...prev,
        positionNameData: result.transformedData,
        isPositionNameDataLoading: false,
      }));
      // Store raw data for mapping
      setPositionRawData(result.rawData);

      // Position will be populated after contract data is fetched
    } catch (error) {
      console.error("Error fetching position data:", error);
      setOriginalPositionNameData([]);
      setPositionNameDataFields((prev) => ({
        ...prev,
        positionNameData: [],
        isPositionNameDataLoading: false,
      }));
    }
  };

  const fetchApprovers = async () => {
    try {
      setApproverDataFields((prev) => ({ ...prev, isApproverDataLoading: true }));

      const result = await fetchApproverDetails(cookiesData, dispatch, editData?.contractMstrKey);

      if (result.success) {
        setOriginalApproverData(result.transformedData);
        setApproverDataFields((prev) => ({
          ...prev,
          approverData: result.transformedData,
          isApproverDataLoading: false,
        }));
      } else {
        console.error("Failed to fetch approver details:", result.error);
        // Fallback to empty array if API fails
        setOriginalApproverData([]);
        setApproverDataFields((prev) => ({
          ...prev,
          approverData: [],
          isApproverDataLoading: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching approver details:", error);
      // Fallback to empty array if API fails
      setOriginalApproverData([]);
      setApproverDataFields((prev) => ({
        ...prev,
        approverData: [],
        isApproverDataLoading: false,
      }));
    }
  };

  // Transform API response to form state
  const transformContractData = (responseData, providerId) => {
    if (!responseData || !Array.isArray(responseData) || responseData.length === 0) {
      return null;
    }

    // Get first item for common fields (all items should have same contract info except tiers)
    const firstItem = responseData[0];

    // Handle rate calculation: Format rate to 2 decimal places
    // NOTE: The stored procedure (sp_select_all_contract_details) divides daily rates by 24 when fetching
    // For daily rates, we multiply by 24 to show the actual daily rate to users
    const calculateRate = (rate, rateUnitType) => {
      if (rate == null || rate === "") return "";
      const n = parseFloat(rate);
      if (isNaN(n)) return "";
      const rateUnit = (rateUnitType || "").toLowerCase();
      const adjustedRate = rateUnit === "daily" ? n * 24 : n;
      return adjustedRate.toFixed(2);
    };

    // Capitalize helper function
    const capitalizeFirst = (str) => {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // Transform tiers based on rate type
    const rateType = capitalizeFirst(firstItem.rate_type);
    let tiers = [];

    if (rateType === "Flat") {
      // For Flat, create single tier from first (and only) object
      const tierItem = firstItem;
      tiers = [
        {
          id: "tier_0",
          tierLevel: tierItem.contract_tier ? `Tier ${tierItem.contract_tier}` : "Tier 1",
          minimumThreshold: tierItem.min_thresold != null ? tierItem.min_thresold.toString() : "",
          maximumThreshold: tierItem.max_thresold != null ? tierItem.max_thresold.toString() : "",
          thresholdUnitType: formatUnitLabel(tierItem.thresold_unit_type),
          rate: calculateRate(tierItem.rate, tierItem.rate_unit_type),
          rateUnitType: formatUnitLabel(tierItem.rate_unit_type),
        },
      ];
    } else {
      // For step/cliff/multisite, group by contract_tier
      const tierMap = new Map();
      responseData.forEach((item, index) => {
        const tierNum = item.contract_tier || index + 1;
        if (!tierMap.has(tierNum)) {
          tierMap.set(tierNum, {
            id: `tier_${tierMap.size}`,
            tierLevel: `Tier ${tierNum}`,
            minimumThreshold: item.min_thresold != null ? item.min_thresold.toString() : "",
            maximumThreshold: item.max_thresold != null ? item.max_thresold.toString() : "",
            thresholdUnitType: formatUnitLabel(item.thresold_unit_type),
            rate: calculateRate(item.rate, item.rate_unit_type),
            rateUnitType: formatUnitLabel(item.rate_unit_type),
          });
        }
      });
      tiers = Array.from(tierMap.values());
    }

    // Transform approvers - collect all approver_details from all response objects
    const approverMap = new Map();
    let hasCompAdmin = false;
    responseData.forEach((item) => {
      if (item.approver_details && Array.isArray(item.approver_details)) {
        item.approver_details.forEach((approver) => {
          // Check if this is the COMP_ADMIN approver
          if (approver.approver_user_key === "COMP_ADMIN" || approver.approver_name === "COMP_ADMIN") {
            hasCompAdmin = true;
            return; // Skip adding COMP_ADMIN to the editable approvers list
          }
          const level = approver.approval_level;
          if (level && !approverMap.has(level)) {
            approverMap.set(level, {
              id: `approver_${approverMap.size}`,
              approverName: approver.approver_name || "",
              isRequired: approver.is_required === 1 ? "Yes" : "No", // API doesn't provide this, may need default
              approverLevel: `Level ${level}`,
            });
          }
        });
      }
    });

    const approvers = Array.from(approverMap.values());
    // Always ensure at least one empty approver if none exist
    if (approvers.length === 0) {
      approvers.push({
        id: "approver_0",
        approverName: "",
        isRequired: "",
        approverLevel: "",
      });
    }

    // Format dates
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      // API returns "2025-10-01 00:00:00", extract just the date part
      return dateStr.split(" ")[0];
    };

    return {
      organizationalData: {
        division: firstItem.division_name || "",
        hospital: firstItem.location_name || "",
        department: firstItem.department || "",
        specialty: firstItem.specialty_name || "",
        facility: firstItem.facility || "",
        physicianNpi: firstItem.npi || "",
        positionName: firstItem.position_name || "",
        employmentType: capitalizeFirst(firstItem.employement_type) || "",
        processLevel: firstItem.process_level || "",
        providerMstrKey: providerId || null,
      },
      contractData: {
        contractType: firstItem.contract_type || "",
        contractName: firstItem.contract_name || "",
        contractStartDate: formatDate(firstItem.contract_start_date),
        contractEndDate: formatDate(firstItem.contract_end_date),
        shiftType: firstItem.shift_type_name || "",
        shiftTypeMstrKey: firstItem.shift_type_mstr_key || null,
        payCode: firstItem.pay_code || "",
        costCenter: firstItem.cost_center || "",
        companyCode: firstItem.company_code,
        spendCategory: firstItem.spend_category || "",
        costingCompany: firstItem.costing_company || "",
        clmContractId: firstItem.clm_contract_id || "",
        gracePeriod: firstItem.grace_period_days,
        rateType: rateType,
        rateTypeMstrKey: firstItem.rate_type_mstr_key || null,
        isTransactional: firstItem.is_transactional === 1 ? "1" : "0",
        shift: firstItem.shift,
        annualCap: firstItem.annual_cap,
        annualCompCap: firstItem.annual_comp_cap,
        periodType: capitalizeFirst(firstItem.period_type),
        contractStructure: rateType,
        tiers: tiers,
        approvers: approvers,
        documents: [],
      },
      hasCompAdmin: hasCompAdmin,
    };
  };

  // Fetch contract data from backend
  const fetchContractData = async () => {
    const contractMstrKeyValue = editData?.contractMstrKey;
    const providerId = editData?.providerId;
    const locationMstrKey = editData?.locationMstrKey;

    if (!contractMstrKeyValue || !providerId || !locationMstrKey) {
      console.warn("Missing contractMstrKey or providerId or locationMstrKey");
      setLoadingState((prev) => ({ ...prev, fetchingContractData: false }));
      return;
    }

    // Store contractMstrKey in state
    setContractMstrKey(contractMstrKeyValue);

    try {
      setLoadingState((prev) => ({ ...prev, fetchingContractData: true }));

      const result = await fetchContractForProvider(
        cookiesData,
        dispatch,
        contractMstrKeyValue,
        providerId,
        locationMstrKey
      );

      if (result.success && result.data && result.data.length > 0) {
        const transformed = transformContractData(result.data, providerId, locationMstrKey);

        if (transformed) {
          // Store initial data for comparison
          const initialOrgData = {
            ...transformed.organizationalData,
            providerMstrKey: providerId,
          };
          setInitialOrganizationalData(initialOrgData);
          setInitialContractData(transformed.contractData);

          // Update organizational data - providerMstrKey is set to providerId
          setOrganizationalData((prev) => ({
            ...transformed.organizationalData,
            providerMstrKey: providerId, // Ensure providerMstrKey is set to providerId for form submission
          }));
          setContractData(transformed.contractData);

          // Set COMP_ADMIN checkbox state based on existing data
          setIncludeCompAdmin(transformed.hasCompAdmin || false);

          // Extract version from response if available (check first item)
          // Note: Version is required by backend validator (min: 1), but may not be in response
          const firstItem = result.data[0];
          if (firstItem?.version !== undefined && firstItem.version !== null) {
            setVersion(firstItem.version);
          } else if (firstItem?.contract_version !== undefined && firstItem.contract_version !== null) {
            setVersion(firstItem.contract_version);
          } else {
            // Default to 1 if version not found (backend requires version >= 1)
            setVersion(1);
          }

          // NPI and position will be populated via useEffect when raw data is available
        }
      } else {
        dispatch(
          setNewMessage({
            message: result.error || "Failed to fetch contract data",
            type: "Error",
            show: true,
          })
        );
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
      dispatch(
        setNewMessage({
          message: "An error occurred while fetching contract data",
          type: "Error",
          show: true,
        })
      );
    } finally {
      setLoadingState((prev) => ({ ...prev, fetchingContractData: false }));
    }
  };

  // Fetch rate type data from API
  const handleFetchRateTypeData = async () => {
    try {
      setIsRateTypeLoading(true);
      const result = await fetchContractRateType(cookiesData, dispatch);

      if (result.success) {
        setRateTypeApiData(result.rateTypeData);
        setFilteredRateTypeData(result.rateTypeData);
      } else {
        console.error("Failed to fetch rate type data:", result.error);
        setRateTypeApiData([]);
        setFilteredRateTypeData([]);
      }
    } catch (error) {
      console.error("Error fetching rate type data:", error);
      setRateTypeApiData([]);
      setFilteredRateTypeData([]);
    } finally {
      setIsRateTypeLoading(false);
    }
  };

  // Fetch shift type data from API
  const handleFetchShiftTypeData = async () => {
    try {
      setIsShiftTypeLoading(true);
      const result = await fetchShiftType(cookiesData, dispatch);

      if (result.success) {
        setShiftTypeData(result.shiftTypeData);
        setFilteredShiftTypeData(result.shiftTypeData);
      } else {
        console.error("Failed to fetch shift type data:", result.error);
        setShiftTypeData([]);
        setFilteredShiftTypeData([]);
      }
    } catch (error) {
      console.error("Error fetching shift type data:", error);
      setShiftTypeData([]);
      setFilteredShiftTypeData([]);
    } finally {
      setIsShiftTypeLoading(false);
    }
  };

  // Fetch all required data on component mount and gate UI with initialLoad
  React.useEffect(() => {
    let isMounted = true;
    const runInitialLoads = async () => {
      try {
        await Promise.all([
          fetchAllDropdownData(),
          handleFetchNpiData(),
          handleFetchPositionData(),
          fetchApprovers(),
          handleFetchRateTypeData(),
          handleFetchShiftTypeData(),
        ]);

        if (editData?.contractMstrKey && editData?.providerId) {
          await fetchContractData();
        }
      } catch (e) {
        // no-op; errors handled in individual functions
      } finally {
        if (isMounted) {
          setLoadingState((prev) => ({ ...prev, initialLoad: false }));
        }
      }
    };
    runInitialLoads();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch contract data when editData is available
  React.useEffect(() => {
    if (editData?.contractMstrKey && editData?.providerId) {
      fetchContractData();
    }
  }, [editData?.contractMstrKey, editData?.providerId]);

  // Create NPI/Position mapping when raw data is available
  React.useEffect(() => {
    if (npiPositionRawData && npiPositionRawData.length > 0) {
      const mapping = createNpiPositionMapping(npiPositionRawData);
      setNpiPositionMap(mapping);
    }
  }, [npiPositionRawData]);

  // Populate position name from provider_id when raw data is available
  React.useEffect(() => {
    // Only run if we have provider key, raw position data, and position name dropdown data
    if (!organizationalData.providerMstrKey || positionRawData.length === 0 || originalPositionNameData.length === 0) {
      return;
    }

    // Find the provider data from position raw data based on provider_mstr_key
    const providerData = positionRawData.find((item) => item.provider_mstr_key === organizationalData.providerMstrKey);

    if (providerData && providerData.org_position_key) {
      // Find the matching position in the dropdown data
      const matchingPosition = originalPositionNameData.find(
        (pos) => pos.id === providerData.org_position_key || pos.text === providerData.position_name
      );

      if (matchingPosition) {
        // Set the position as selected in the dropdown
        setPositionNameDataFields((prev) => ({
          ...prev,
          positionNameDataValue: [matchingPosition],
        }));

        // Update organizational data with the position name
        setOrganizationalData((prev) => ({
          ...prev,
          positionName: matchingPosition.text,
        }));
      }
    }
  }, [organizationalData.providerMstrKey, positionRawData, originalPositionNameData]);

  // Update NPI dropdown selection when organizationalData.physicianNpi changes (fallback)
  // Commented out - Employee NPI is no longer required
  /* React.useEffect(() => {
        if (organizationalData.physicianNpi && originalNpiData.length > 0 && npiDataFields.npiDataValue.length === 0) {
            const matchingNpi = originalNpiData.find(
                npi => npi.text === organizationalData.physicianNpi || npi.id === organizationalData.physicianNpi
            );
            if (matchingNpi) {
                setNpiDataFields(prev => ({
                    ...prev,
                    npiDataValue: [matchingNpi]
                }));
            }
        }
    }, [organizationalData.physicianNpi, originalNpiData]); */

  // Update Position dropdown selection when organizationalData.positionName changes (fallback)
  React.useEffect(() => {
    if (
      organizationalData.positionName &&
      originalPositionNameData.length > 0 &&
      positionNameDataFields.positionNameDataValue.length === 0
    ) {
      const matchingPosition = originalPositionNameData.find(
        (pos) => pos.text === organizationalData.positionName || pos.id === organizationalData.positionName
      );
      if (matchingPosition) {
        setPositionNameDataFields((prev) => ({
          ...prev,
          positionNameDataValue: [matchingPosition],
        }));
      }
    }
  }, [organizationalData.positionName, originalPositionNameData]);

  // Compare current data with initial data to determine if changes have been made
  React.useEffect(() => {
    if (initialOrganizationalData && initialContractData) {
      // Deep comparison function for nested objects and arrays
      const deepEqual = (obj1, obj2) => {
        if (obj1 === obj2) return true;
        if (obj1 == null || obj2 == null) return false;
        if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) return false;

        for (let key of keys1) {
          if (!keys2.includes(key)) return false;

          if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
            if (obj1[key].length !== obj2[key].length) return false;
            for (let i = 0; i < obj1[key].length; i++) {
              if (!deepEqual(obj1[key][i], obj2[key][i])) return false;
            }
          } else if (
            typeof obj1[key] === "object" &&
            typeof obj2[key] === "object" &&
            obj1[key] !== null &&
            obj2[key] !== null
          ) {
            if (!deepEqual(obj1[key], obj2[key])) return false;
          } else if (obj1[key] !== obj2[key]) {
            return false;
          }
        }

        return true;
      };

      const orgDataChanged = !deepEqual(organizationalData, initialOrganizationalData);
      const contractDataChanged = !deepEqual(contractData, initialContractData);

      setHasChanges(orgDataChanged || contractDataChanged);
    }
  }, [organizationalData, contractData, initialOrganizationalData, initialContractData]);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields first
    const errors = validateRequiredFields();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Clear validation errors if validation passes
    setValidationErrors({});

    // Validate Tier Level selections: required and unique
    const selectedLevels = contractData.tiers.map((t) => {
      const m = t.tierLevel?.match(/\d+/);
      return m ? parseInt(m[0]) : null;
    });

    if (selectedLevels.some((v) => v === null)) {
      dispatch(
        setNewMessage({
          message: "Please select a Tier Level for each tier.",
          type: "Error",
          show: true,
        })
      );
      return;
    }

    const unique = new Set(selectedLevels);
    if (unique.size !== selectedLevels.length) {
      dispatch(
        setNewMessage({
          message: "Tier Levels must be unique across tiers.",
          type: "Error",
          show: true,
        })
      );
      return;
    }

    // Validate single tier must be Tier 1
    if (selectedLevels.length === 1 && selectedLevels[0] !== 1) {
      dispatch(
        setNewMessage({
          message: "When there is only one tier, it must be Tier 1.",
          type: "Error",
          show: true,
        })
      );
      return;
    }

    // Validate sequential tier levels when multiple tiers exist
    if (selectedLevels.length > 1) {
      const tierNumbers = selectedLevels.slice().sort((a, b) => a - b);
      const isSequential = tierNumbers.every((num, idx) => num === idx + 1);
      if (!isSequential) {
        dispatch(
          setNewMessage({
            message: "Tier Levels must be sequential starting from Tier 1 (e.g., Tier 1, Tier 2, Tier 3).",
            type: "Error",
            show: true,
          })
        );
        return;
      }
    }

    // Validate Approver Level selections: sequential when multiple approvers exist
    const approverLevels = contractData.approvers
      .map((a) => {
        const m = a.approverLevel?.match(/\d+/);
        return m ? parseInt(m[0]) : null;
      })
      .filter((level) => level !== null);

    if (approverLevels.length > 1) {
      const uniqueApproverLevels = [...new Set(approverLevels)].sort((a, b) => a - b);
      const isApproverSequential = uniqueApproverLevels.every((num, idx) => num === idx + 1);
      if (!isApproverSequential) {
        dispatch(
          setNewMessage({
            message: "Approver Levels must be sequential starting from Level 1 (e.g., Level 1, Level 2).",
            type: "Error",
            show: true,
          })
        );
        return;
      }
    }

    const formData = collectFormData();

    // Call the update contract API
    handleUpdateContract(formData);
  };

  // Handle contract update API call
  const handleUpdateContract = async (formData) => {
    try {
      setLoadingState((prev) => ({ ...prev, submitting: true }));
      const cookiesData = getUserCredential();

      // Call the update contract API
      const result = await updateContract(cookiesData, formData, dispatch);

      if (result.success) {
        // Refresh data and return to table view
        if (onUpdateContract) {
          await onUpdateContract(result.data || formData);
        } else {
          setMode("Default");
        }

        dispatch(
          setNewMessage({
            message: result.message || "Contract updated successfully!",
            type: "Success",
            show: true,
          })
        );
      } else {
        // Show error toast
        dispatch(
          setNewMessage({
            message: result.error || "Failed to update contract",
            type: "Error",
            show: true,
          })
        );
      }
    } catch (error) {
      console.error("Error updating contract:", error);
      dispatch(
        setNewMessage({
          message: "An unexpected error occurred while updating the contract",
          type: "Error",
          show: true,
        })
      );
    } finally {
      // Reset loading state
      setLoadingState((prev) => ({ ...prev, submitting: false }));
    }
  };

  // Handle back navigation
  const handleBack = () => {
    setMode("Default");
    if (setIsUpdate) {
      setIsUpdate(true);
    }
  };

  // Drag and drop handlers
  const handleParentDragStart = (e, id) => {
    e.dataTransfer.setData("text/plain", id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleParentDrop = (e, dropId) => {
    e.preventDefault();
    if (e.target.id === "header") {
      const dragId = e.dataTransfer.getData("text/plain");
      const updatedCategories = contractCategories.slice();
      const dragIndex = contractCategories.findIndex((x) => x.category_config_key === Number(dragId));
      const dropIndex = contractCategories.findIndex((x) => x.category_config_key === dropId);

      if (dragIndex !== dropIndex && dragIndex !== -1 && dropIndex !== -1) {
        const [draggedCategory] = updatedCategories.splice(dragIndex, 1);
        updatedCategories.splice(dropIndex, 0, draggedCategory);
        setContractCategories(updatedCategories);
      }
    }
  };

  // Render organizational attributes
  const renderOrganizationalAttributes = () => {
    // Get fields excluding Employee NPI, Position Name, and Employment Type
    const orgFields = contractCategories.find((cat) => cat.category_code === "org_attr")?.fields || [];
    const fieldsWithoutEmployee = orgFields.filter(
      (field) =>
        field.filter_column_name !== "physicianNpi" &&
        field.filter_column_name !== "positionName" &&
        field.filter_column_name !== "employmentType"
    );

    return (
      <div className="form-row providerbox">
        <ListBox
          style={{ width: "100%" }}
          data={fieldsWithoutEmployee}
          item={(props) => (
            <ContractFields
              {...props}
              isFieldDisabled={isFieldDisabled}
              organizationalData={organizationalData}
              contractData={contractData}
              onOrganizationalDataChange={updateOrganizationalData}
              onContractDataChange={updateContractData}
              dropdownData={dropdownData}
              validationErrors={validationErrors}
              onRefreshDropdownData={handleRefreshDropdownData}
            />
          )}
        />

        {/* Employee NPI Field - Rendered Separately */}
        {/* Commented out - Employee NPI field is no longer required */}
        {/* <div className="fullwidthelm" data-field="providerMstrKey">
                    <label className="provider-elm">
                        Employee NPI*
                    </label>
                    <CustomDropdown
                        name="npiDataValue"
                        className="provider-elm"
                        dataItemKey="id"
                        textField="text"
                        data={npiDataFields.npiData}
                        dataList={originalNpiData}
                        valueData={npiDataFields.npiDataValue.length > 0 ? npiDataFields.npiDataValue[0] : ""}
                        autoClose={false}
                        autoBind={true}
                        loading={npiDataFields.isNpiDataLoading}
                        disabled={npiDataFields.isNpiDataLoading}
                        onOpen={() =>
                            onOpenChange(
                                setNpiDataFields,
                                originalNpiData,
                                "npiData",
                                npiDataFields,
                                npiDataFields.npiDataValue
                            )
                        }
                        onChange={(value) => handleNpiChange(value ? [value] : [])}
                        onFilterChange={(filteredData) => {
                            setNpiDataFields((prev) => ({
                                ...prev,
                                npiData: filteredData
                            }));
                        }}
                        onClose={() =>
                            setNpiDataFields((prev) => ({
                                ...prev,
                                npiData: originalNpiData,
                                filterValue: "",
                                search: "",
                            }))
                        }
                        filterable={true}
                        placeholder="Select employee NPI"
                    />
                    {validationErrors['providerMstrKey'] && (
                        <p className="error-msg">
                            {validationErrors['providerMstrKey']}
                        </p>
                    )}
                </div> */}

        {/* Position Name Field - Rendered Separately */}
        <div className="fullwidthelm" data-field="positionName">
          <label className="provider-elm">Position Name*</label>
          <CustomDropdown
            name="positionNameDataValue"
            className="provider-elm"
            dataItemKey="id"
            textField="text"
            data={positionNameDataFields.positionNameData}
            dataList={originalPositionNameData}
            valueData={
              positionNameDataFields.positionNameDataValue.length > 0
                ? positionNameDataFields.positionNameDataValue[0]
                : ""
            }
            autoClose={false}
            autoBind={true}
            loading={positionNameDataFields.isPositionNameDataLoading}
            disabled={positionNameDataFields.isPositionNameDataLoading}
            onOpen={() =>
              onOpenChange(
                setPositionNameDataFields,
                originalPositionNameData,
                "positionNameData",
                positionNameDataFields,
                positionNameDataFields.positionNameDataValue
              )
            }
            onChange={(value) => handlePositionChange(value ? [value] : [])}
            onFilterChange={(filteredData) => {
              setPositionNameDataFields((prev) => ({
                ...prev,
                positionNameData: filteredData,
              }));
            }}
            onClose={() =>
              setPositionNameDataFields((prev) => ({
                ...prev,
                positionNameData: originalPositionNameData,
                filterValue: "",
                search: "",
              }))
            }
            filterable={true}
            placeholder="Select position name"
          />
          {validationErrors["positionName"] && <p className="error-msg">{validationErrors["positionName"]}</p>}
        </div>

        {/* Employment Type Field - Rendered Separately */}
        <div className="fullwidthelm" data-field="employmentType">
          <label className="provider-elm">Employment Type{!isLeeHealthTenant && "*"}</label>
          <CustomDropdown
            data={filteredEmploymentTypeData}
            dataList={dummyEmploymentTypeData}
            textField="text"
            dataItemKey="text"
            className="provider-elm"
            valueData={
              organizationalData.employmentType
                ? { text: organizationalData.employmentType, id: organizationalData.employmentType.toLowerCase() }
                : null
            }
            onChange={(e) => {
              updateOrganizationalData("employmentType", e?.text || "");
              setFilteredEmploymentTypeData(dummyEmploymentTypeData);
            }}
            onFilterChange={(filteredData) => setFilteredEmploymentTypeData(filteredData)}
            onOpen={(data) => setFilteredEmploymentTypeData(data)}
            onClose={() => setFilteredEmploymentTypeData(dummyEmploymentTypeData)}
            filterable={true}
            autoClose={false}
            autoBind={true}
            placeholder="Select employment type"
          />
          {validationErrors["employmentType"] && <p className="error-msg">{validationErrors["employmentType"]}</p>}
        </div>

        {/* Process Level Field - CS03 only */}
        {isCmnspiritCs03Tenant && (
          <div className="fullwidthelm">
            <label className="provider-elm">Process Level</label>
            <Input
              type="text"
              className="provider-elm"
              value={organizationalData.processLevel || ""}
              onChange={(e) => {
                const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                updateOrganizationalData("processLevel", value);
              }}
              placeholder="Enter process level"
            />
          </div>
        )}

        {/* Department Field - CS03 only */}
        {isCmnspiritCs03Tenant && (
          <div className="fullwidthelm">
            <label className="provider-elm">Department</label>
            <Input
              type="text"
              className="provider-elm"
              value={organizationalData.department || ""}
              onChange={(e) => {
                const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                updateOrganizationalData("department", value);
              }}
              placeholder="Enter department"
            />
          </div>
        )}
      </div>
    );
  };

  // Render contract attributes
  const renderContractAttributes = () => {
    return (
      <div className="contract-attributes-section">
        {/* <div className="contract-header">
                    <h4>Contract</h4>
                </div> */}

        <div className="contract-details-container">
          {/* Contract Details */}
          <div className="contract-details-section" style={{ overflowY: "auto" }}>
            <h5 className="contract-details-header">CONTRACT DETAILS</h5>
            <div className="form-row providerbox">
              <ListBox
                style={{ width: "100%" }}
                data={contractCategories.find((cat) => cat.category_code === "contract_attr")?.fields.slice(0, 4) || []}
                item={(props) => (
                  <ContractFields
                    {...props}
                    isFieldDisabled={isFieldDisabled}
                    organizationalData={organizationalData}
                    contractData={contractData}
                    onOrganizationalDataChange={updateOrganizationalData}
                    onContractDataChange={updateContractData}
                    dropdownData={dropdownData}
                    validationErrors={validationErrors}
                    onRefreshDropdownData={handleRefreshDropdownData}
                  />
                )}
              />

              {/* Shift Type Field */}
              <div className="fullwidthelm" data-field="shiftType">
                <label className="provider-elm">Shift Type*</label>
                <CustomDropdown
                  data={filteredShiftTypeData}
                  dataList={shiftTypeData}
                  textField="text"
                  dataItemKey="id"
                  className="provider-elm"
                  loading={isShiftTypeLoading}
                  disabled={isShiftTypeLoading}
                  valueData={
                    contractData.shiftTypeMstrKey
                      ? shiftTypeData.find((st) => st.id === contractData.shiftTypeMstrKey) || null
                      : null
                  }
                  onChange={(e) => {
                    const value = e?.text || "";
                    const mstrKey = e?.id || null;
                    setContractData((prev) => ({
                      ...prev,
                      shiftType: value,
                      shiftTypeMstrKey: mstrKey,
                    }));
                    if (validationErrors["shiftType"]) {
                      setValidationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors["shiftType"];
                        return newErrors;
                      });
                    }
                    setFilteredShiftTypeData(shiftTypeData);
                  }}
                  onFilterChange={(filteredData) => setFilteredShiftTypeData(filteredData)}
                  onOpen={(data) => setFilteredShiftTypeData(data)}
                  onClose={() => setFilteredShiftTypeData(shiftTypeData)}
                  filterable={true}
                  autoClose={false}
                  autoBind={true}
                />
                {validationErrors["shiftType"] && <p className="error-msg">{validationErrors["shiftType"]}</p>}
              </div>

              {/* PayCode Field */}
              <div className="fullwidthelm" data-field="payCode">
                <label className="provider-elm">Pay Code{isCmnspiritTenant && "*"}</label>
                <Input
                  type="text"
                  className="provider-elm"
                  value={contractData.payCode || ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    updateContractData("payCode", value);
                  }}
                  placeholder="Enter pay code"
                />
                {validationErrors["payCode"] && <p className="error-msg">{validationErrors["payCode"]}</p>}
              </div>

              {/* Cost Center Field */}
              <div className="fullwidthelm" data-field="costCenter">
                <label className="provider-elm">Cost Center{isCmnspiritTenant && "*"}</label>
                <Input
                  type="text"
                  className="provider-elm"
                  value={contractData.costCenter || ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    updateContractData("costCenter", value);
                  }}
                  placeholder="Enter cost center"
                />
                {validationErrors["costCenter"] && <p className="error-msg">{validationErrors["costCenter"]}</p>}
              </div>

              {/* Company Code Field */}
              <div className="fullwidthelm" data-field="companyCode">
                <label className="provider-elm">Company Code{isCmnspiritTenant && "*"}</label>
                <Input
                  type="text"
                  className="provider-elm"
                  value={contractData.companyCode ?? ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    // Filter to allow only numeric characters
                    const numericValue = value.replace(/[^0-9]/g, "");
                    updateContractData("companyCode", numericValue);
                  }}
                  placeholder="Enter company code"
                />
                {validationErrors["companyCode"] && <p className="error-msg">{validationErrors["companyCode"]}</p>}
              </div>

              {/* Spend Category Field */}
              <div className="fullwidthelm">
                <label className="provider-elm">Spend Category</label>
                <Input
                  type="text"
                  className="provider-elm"
                  value={contractData.spendCategory || ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    updateContractData("spendCategory", value);
                  }}
                  placeholder="Enter spend category"
                />
              </div>

              {/* Costing Company Field */}
              <div className="fullwidthelm">
                <label className="provider-elm">Costing Company</label>
                <Input
                  type="text"
                  className="provider-elm"
                  value={contractData.costingCompany || ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    updateContractData("costingCompany", value);
                  }}
                  placeholder="Enter costing company"
                />
              </div>

              {/* Contract ID Field (clm_contract_id) */}
              <div className="fullwidthelm" data-field="clmContractId">
                <label className="provider-elm">Contract ID{isCmnspiritTenant && "*"}</label>
                <Input
                  type="text"
                  className="provider-elm"
                  value={contractData.clmContractId || ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    updateContractData("clmContractId", value);
                  }}
                  placeholder="Enter Contract ID"
                />
                {validationErrors["clmContractId"] && <p className="error-msg">{validationErrors["clmContractId"]}</p>}
              </div>

              {/* Grace Period Field */}
              <div className="fullwidthelm">
                <label className="provider-elm">Grace Period (Days)</label>
                <Input
                  type="number"
                  min="0"
                  className="provider-elm"
                  value={contractData.gracePeriod ?? ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    updateContractData("gracePeriod", value);
                  }}
                  placeholder="Enter grace period in days"
                />
                {validationErrors.gracePeriod && <p className="error-msg">{validationErrors.gracePeriod}</p>}
              </div>

              {/* Is Transactional Field */}
              <div className="fullwidthelm">
                <label className="provider-elm">Is Transactional</label>
                <CustomDropdown
                  data={filteredIsTransactionalData}
                  dataList={dummyIsTransactionalData}
                  textField="text"
                  dataItemKey="text"
                  className="provider-elm"
                  valueData={
                    contractData.isTransactional
                      ? {
                          text: contractData.isTransactional,
                          id: contractData.isTransactional.toLowerCase().replace(" ", "_"),
                        }
                      : null
                  }
                  onChange={(e) => {
                    updateContractData("isTransactional", e?.text || "");
                    setFilteredIsTransactionalData(dummyIsTransactionalData);
                  }}
                  onFilterChange={(filteredData) => setFilteredIsTransactionalData(filteredData)}
                  onOpen={(data) => setFilteredIsTransactionalData(data)}
                  onClose={() => setFilteredIsTransactionalData(dummyIsTransactionalData)}
                  filterable={true}
                  autoClose={false}
                  autoBind={true}
                />
              </div>

              {/* Shift Field */}
              <div className="fullwidthelm">
                <label className="provider-elm">Shift</label>
                <Input
                  type="number"
                  min="0"
                  max="24"
                  className="provider-elm"
                  value={contractData.shift ?? ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    updateContractData("shift", value);
                  }}
                  placeholder="Enter hours (0-24)"
                />
                {validationErrors.shift && <p className="error-msg">{validationErrors.shift}</p>}
              </div>

              {/* Annual Cap Field */}
              <div className="fullwidthelm">
                <label className="provider-elm">Annual Cap</label>
                <Input
                  type="number"
                  className="provider-elm"
                  value={contractData.annualCap ?? ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    updateContractData("annualCap", value);
                  }}
                  placeholder="Enter annual cap"
                />
              </div>

              {/* Annual Comp Cap Field */}
              <div className="fullwidthelm">
                <label className="provider-elm">Annual Comp Cap</label>
                <Input
                  type="number"
                  className="provider-elm"
                  value={contractData.annualCompCap ?? ""}
                  onChange={(e) => {
                    const value = e.value !== undefined ? e.value : e.target ? e.target.value : "";
                    updateContractData("annualCompCap", value);
                  }}
                  placeholder="Enter annual comp cap"
                />
              </div>

              {/* Period Type Field */}
              <div className="fullwidthelm" data-field="periodType">
                <label className="provider-elm">Period Type*</label>
                <CustomDropdown
                  data={filteredPeriodTypeData}
                  dataList={dummyPeriodTypeData}
                  textField="text"
                  dataItemKey="text"
                  className="provider-elm"
                  valueData={
                    contractData.periodType
                      ? { text: contractData.periodType, id: contractData.periodType.toLowerCase() }
                      : null
                  }
                  onChange={(e) => {
                    updateContractData("periodType", e?.text || "");
                    setFilteredPeriodTypeData(dummyPeriodTypeData);
                  }}
                  onFilterChange={(filteredData) => setFilteredPeriodTypeData(filteredData)}
                  onOpen={(data) => setFilteredPeriodTypeData(data)}
                  onClose={() => setFilteredPeriodTypeData(dummyPeriodTypeData)}
                  filterable={true}
                  autoClose={false}
                  autoBind={true}
                />
                {validationErrors["periodType"] && <p className="error-msg">{validationErrors["periodType"]}</p>}
              </div>
            </div>
          </div>
          <div className="contract-tier-structure-section">
            {/* Contract Structure Details */}
            <div className="contract-structure-section">
              <h5 className="contract-details-header">CONTRACT STRUCTURE DETAILS</h5>
              <div className="form-row providerbox">
                <div className="fullwidthelm" data-field="rateType">
                  <label className="provider-elm">Rate Type*</label>
                  <CustomDropdown
                    data={filteredRateTypeData}
                    dataList={rateTypeApiData}
                    textField="text"
                    dataItemKey="id"
                    className="provider-elm"
                    loading={isRateTypeLoading}
                    disabled={isRateTypeLoading}
                    valueData={
                      contractData.rateTypeMstrKey
                        ? rateTypeApiData.find((rt) => rt.id === contractData.rateTypeMstrKey) || null
                        : null
                    }
                    onChange={(e) => {
                      const value = e?.text || "";
                      const mstrKey = e?.id || null;

                      // Validate rate type change before updating
                      const validation = validateRateTypeChange(
                        contractData.rateType,
                        value,
                        contractData.tiers.length
                      );

                      if (!validation.isValid) {
                        // Show error message and prevent update
                        dispatch(
                          setNewMessage({
                            message: validation.message,
                            type: "Error",
                            show: true,
                          })
                        );
                        return;
                      }

                      setContractData((prev) => ({
                        ...prev,
                        rateType: value,
                        rateTypeMstrKey: mstrKey,
                        contractStructure: value,
                      }));
                      if (validationErrors["rateType"]) {
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors["rateType"];
                          return newErrors;
                        });
                      }
                      setFilteredRateTypeData(rateTypeApiData);
                    }}
                    onFilterChange={(filteredData) => setFilteredRateTypeData(filteredData)}
                    onOpen={(data) => setFilteredRateTypeData(data)}
                    onClose={() => setFilteredRateTypeData(rateTypeApiData)}
                    filterable={true}
                    autoClose={false}
                    autoBind={true}
                  />
                  {validationErrors["rateType"] && <p className="error-msg">{validationErrors["rateType"]}</p>}
                </div>
              </div>
            </div>

            {/* Tiers Section */}
            <div className="tier-structure-section">
              <div className="tiers-section approver-expansion-panel">
                <div className="contract-structure-header">
                  <h5 className="contract-details-header">TIER ATTRIBUTES</h5>
                  {contractData.rateType !== "Flat" &&
                    contractData.contractStructure !== "Flat" &&
                    contractData.tiers.length < 5 && (
                      <button className="add-new-btn" onClick={addNewTier}>
                        + Add New Tier
                      </button>
                    )}
                </div>
                {contractData.tiers.map((tier, index) => (
                  <ExpansionPanel
                    key={tier.id}
                    title="Tier Details"
                    expanded={expandedTier === tier.id}
                    tabIndex={0}
                    onAction={(event) => {
                      setExpandedTier(event.expanded ? "" : tier.id);
                    }}
                  >
                    <Reveal>
                      {expandedTier === tier.id && (
                        <ExpansionPanelContent>
                          <div
                            className="tier-fields"
                            onKeyDown={(e) => {
                              if (e.key === " " || e.keyCode === 32) {
                                e.stopPropagation();
                              }
                            }}
                          >
                            {/* Tier Level Field */}
                            <div className="fullwidthelm" data-field={`tier_${index}_tierLevel`}>
                              <label className="provider-elm">Tier Level*</label>
                              {(() => {
                                const usedLevels = contractData.tiers
                                  .map((t, i) => (i === index ? null : t.tierLevel))
                                  .filter(Boolean);
                                const allTierOptions = Array.from({ length: 5 }, (_, i) => ({
                                  text: `Tier ${i + 1}`,
                                  id: i + 1,
                                }));
                                const options = allTierOptions.filter(
                                  (opt) => !usedLevels.includes(opt.text) || opt.text === tier.tierLevel
                                );
                                // Always use fresh options as base, only use filtered data if user is actively filtering
                                const currentFilteredData = filteredTierLevelData[index]
                                  ? filteredTierLevelData[index].filter((opt) => options.some((o) => o.id === opt.id))
                                  : options;
                                return (
                                  <CustomDropdown
                                    data={currentFilteredData}
                                    dataList={options}
                                    textField="text"
                                    dataItemKey="id"
                                    className="provider-elm"
                                    valueData={
                                      tier.tierLevel
                                        ? {
                                            text: tier.tierLevel,
                                            id: parseInt((tier.tierLevel.match(/\d+/) || [null])[0]),
                                          }
                                        : null
                                    }
                                    onChange={(e) => {
                                      updateTierData(index, "tierLevel", e?.text || "");
                                    }}
                                    onFilterChange={(filteredData) =>
                                      setFilteredTierLevelData((prev) => ({ ...prev, [index]: filteredData }))
                                    }
                                    onOpen={(data) => setFilteredTierLevelData((prev) => ({ ...prev, [index]: data }))}
                                    onClose={() =>
                                      setFilteredTierLevelData((prev) => {
                                        const newState = { ...prev };
                                        delete newState[index];
                                        return newState;
                                      })
                                    }
                                    filterable={true}
                                    autoClose={false}
                                    autoBind={true}
                                    placeholder="Select tier level"
                                  />
                                );
                              })()}
                              {validationErrors[`tier_${index}_tierLevel`] && (
                                <p className="error-msg">{validationErrors[`tier_${index}_tierLevel`]}</p>
                              )}
                            </div>

                            {/* Minimum Threshold Field */}
                            <div className="fullwidthelm">
                              <label className="provider-elm">Minimum Threshold</label>
                              <Input
                                type="number"
                                className="provider-elm"
                                value={tier.minimumThreshold ?? ""}
                                onChange={(e) => updateTierData(index, "minimumThreshold", e.value)}
                                placeholder="Enter minimum threshold"
                              />
                            </div>

                            {/* Maximum Threshold Field */}
                            <div className="fullwidthelm">
                              <label className="provider-elm">Maximum Threshold</label>
                              <Input
                                type="number"
                                className="provider-elm"
                                value={tier.maximumThreshold ?? ""}
                                onChange={(e) => updateTierData(index, "maximumThreshold", e.value)}
                                placeholder="Enter maximum threshold"
                              />
                            </div>

                            {/* Threshold Unit Type Field */}
                            <div className="fullwidthelm" data-field={`tier_${index}_thresholdUnitType`}>
                              <label className="provider-elm">Threshold Unit Type*</label>
                              <CustomDropdown
                                data={filteredThresholdUnitTypeData[index] || dummyThresholdUnitTypeData}
                                dataList={dummyThresholdUnitTypeData}
                                textField="text"
                                dataItemKey="text"
                                className="provider-elm"
                                valueData={
                                  tier.thresholdUnitType
                                    ? {
                                        text: tier.thresholdUnitType,
                                        id: tier.thresholdUnitType.toLowerCase().replace(" ", "_"),
                                      }
                                    : null
                                }
                                onChange={(e) => {
                                  updateTierData(index, "thresholdUnitType", e?.text || "");
                                  setFilteredThresholdUnitTypeData((prev) => ({
                                    ...prev,
                                    [index]: dummyThresholdUnitTypeData,
                                  }));
                                }}
                                onFilterChange={(filteredData) =>
                                  setFilteredThresholdUnitTypeData((prev) => ({ ...prev, [index]: filteredData }))
                                }
                                onOpen={(data) =>
                                  setFilteredThresholdUnitTypeData((prev) => ({ ...prev, [index]: data }))
                                }
                                onClose={() =>
                                  setFilteredThresholdUnitTypeData((prev) => ({
                                    ...prev,
                                    [index]: dummyThresholdUnitTypeData,
                                  }))
                                }
                                filterable={true}
                                autoClose={false}
                                autoBind={true}
                              />
                              {validationErrors[`tier_${index}_thresholdUnitType`] && (
                                <p className="error-msg">{validationErrors[`tier_${index}_thresholdUnitType`]}</p>
                              )}
                            </div>

                            {/* Rate Field */}
                            <div className="fullwidthelm">
                              <label className="provider-elm">Rate</label>
                              <Input
                                type="text"
                                className="provider-elm"
                                value={tier.rate != null ? tier.rate : ""}
                                onChange={(e) => {
                                  const value = e.value;
                                  // Allow only numbers and a single decimal point
                                  let numericValue = value.replace(/[^0-9.]/g, "");
                                  // Ensure only one decimal point
                                  const parts = numericValue.split(".");
                                  if (parts.length > 2) {
                                    numericValue = parts[0] + "." + parts.slice(1).join("");
                                  }
                                  // Limit to 3 decimal places
                                  if (parts.length === 2 && parts[1].length > 3) {
                                    numericValue = parts[0] + "." + parts[1].substring(0, 3);
                                  }
                                  updateTierData(index, "rate", numericValue);
                                }}
                                onBlur={(e) => {
                                  // Format to 2 decimal places when user finishes editing
                                  const value = e.value;
                                  if (value && value.trim() !== "") {
                                    const n = parseFloat(value);
                                    if (!isNaN(n)) {
                                      updateTierData(index, "rate", n.toFixed(2));
                                    } else {
                                      // If invalid, clear it
                                      updateTierData(index, "rate", "");
                                    }
                                  }
                                }}
                                placeholder="Enter rate"
                              />
                            </div>

                            {/* Rate Unit Type Field */}
                            <div className="fullwidthelm" data-field={`tier_${index}_rateUnitType`}>
                              <label className="provider-elm">Rate Unit Type*</label>
                              <CustomDropdown
                                data={filteredRateUnitTypeData[index] || dummyRateUnitTypeData}
                                dataList={dummyRateUnitTypeData}
                                textField="text"
                                dataItemKey="text"
                                className="provider-elm"
                                valueData={
                                  tier.rateUnitType
                                    ? { text: tier.rateUnitType, id: tier.rateUnitType.toLowerCase().replace(" ", "_") }
                                    : null
                                }
                                onChange={(e) => {
                                  updateTierData(index, "rateUnitType", e?.text || "");
                                  setFilteredRateUnitTypeData((prev) => ({ ...prev, [index]: dummyRateUnitTypeData }));
                                }}
                                onFilterChange={(filteredData) =>
                                  setFilteredRateUnitTypeData((prev) => ({ ...prev, [index]: filteredData }))
                                }
                                onOpen={(data) => setFilteredRateUnitTypeData((prev) => ({ ...prev, [index]: data }))}
                                onClose={() =>
                                  setFilteredRateUnitTypeData((prev) => ({ ...prev, [index]: dummyRateUnitTypeData }))
                                }
                                filterable={true}
                                autoClose={false}
                                autoBind={true}
                              />
                              {validationErrors[`tier_${index}_rateUnitType`] && (
                                <p className="error-msg">{validationErrors[`tier_${index}_rateUnitType`]}</p>
                              )}
                            </div>

                            <div className="approver-delete-section">
                              {contractData.tiers.length > 1 && index > 0 && (
                                <Tooltip position="bottom" anchorElement="target">
                                  <button
                                    className="remove-btn-full"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeTier(index);
                                    }}
                                    title="Delete Tier"
                                  >
                                    Delete Tier
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        </ExpansionPanelContent>
                      )}
                    </Reveal>
                  </ExpansionPanel>
                ))}
              </div>
            </div>
          </div>

          <div className="contract-structure-container">
            {/* Approver Attributes */}
            <div className="approver-attributes-section approver-expansion-panel">
              <div className="contract-structure-header">
                <h5 className="contract-details-header">APPROVER ATTRIBUTES</h5>
                {contractData.approvers.length < 2 && (
                  <button className="add-new-btn" onClick={addNewApprover}>
                    + Add New Approver
                  </button>
                )}
              </div>

              {contractData.approvers.map((approver, index) => (
                <ExpansionPanel
                  key={approver.id}
                  title="Approver Details"
                  expanded={expandedApprover === approver.id}
                  tabIndex={0}
                  onAction={(event) => {
                    setExpandedApprover(event.expanded ? "" : approver.id);
                  }}
                >
                  <Reveal>
                    {expandedApprover === approver.id && (
                      <ExpansionPanelContent>
                        <div
                          className="approver-fields"
                          onKeyDown={(e) => {
                            if (e.key === " " || e.keyCode === 32) {
                              e.stopPropagation();
                            }
                          }}
                        >
                          {/* Approver Level Field */}
                          <div className="fullwidthelm" data-field={`approver_${index}_approverLevel`}>
                            <label className="provider-elm">Approver Level*</label>
                            {(() => {
                              const usedLevels = contractData.approvers
                                .map((a, i) => (i === index ? null : a.approverLevel))
                                .filter(Boolean);
                              const allApproverLevelOptions = [
                                { text: "Level 1", id: "level_1" },
                                { text: "Level 2", id: "level_2" },
                              ];
                              const options = allApproverLevelOptions.filter(
                                (opt) => !usedLevels.includes(opt.text) || opt.text === approver.approverLevel
                              );
                              // Always use fresh options as base, only use filtered data if user is actively filtering
                              const currentFilteredApproverData = filteredApproverLevelData[index]
                                ? filteredApproverLevelData[index].filter((opt) => options.some((o) => o.id === opt.id))
                                : options;
                              return (
                                <CustomDropdown
                                  data={currentFilteredApproverData}
                                  dataList={options}
                                  textField="text"
                                  dataItemKey="id"
                                  className="provider-elm"
                                  valueData={
                                    approver.approverLevel
                                      ? {
                                          text: approver.approverLevel,
                                          id: approver.approverLevel.toLowerCase().replace(" ", "_"),
                                        }
                                      : null
                                  }
                                  onChange={(e) => {
                                    updateApproverData(index, "approverLevel", e?.text || "");
                                  }}
                                  onFilterChange={(filteredData) =>
                                    setFilteredApproverLevelData((prev) => ({ ...prev, [index]: filteredData }))
                                  }
                                  onOpen={(data) =>
                                    setFilteredApproverLevelData((prev) => ({ ...prev, [index]: data }))
                                  }
                                  onClose={() =>
                                    setFilteredApproverLevelData((prev) => {
                                      const newState = { ...prev };
                                      delete newState[index];
                                      return newState;
                                    })
                                  }
                                  filterable={true}
                                  autoClose={false}
                                  autoBind={true}
                                  placeholder="Select approver level"
                                />
                              );
                            })()}
                            {validationErrors[`approver_${index}_approverLevel`] && (
                              <p className="error-msg">{validationErrors[`approver_${index}_approverLevel`]}</p>
                            )}
                          </div>

                          {/* Approver Name Field */}
                          <div className="fullwidthelm" data-field={`approver_${index}_approverName`}>
                            <label className="provider-elm">Approver Name*</label>
                            <CustomDropdown
                              name={`approverName_${index}`}
                              className="provider-elm"
                              dataItemKey="id"
                              textField="text"
                              data={approverDataFields.approverData}
                              dataList={originalApproverData}
                              valueData={
                                approver.approverName
                                  ? originalApproverData.find((apr) => apr.text === approver.approverName) || ""
                                  : ""
                              }
                              autoClose={false}
                              autoBind={true}
                              loading={approverDataFields.isApproverDataLoading}
                              disabled={approverDataFields.isApproverDataLoading}
                              onOpen={() =>
                                onOpenChange(
                                  setApproverDataFields,
                                  originalApproverData,
                                  "approverData",
                                  approverDataFields,
                                  approver.approverName
                                    ? originalApproverData.filter((apr) => apr.text === approver.approverName)
                                    : []
                                )
                              }
                              onChange={(value) => handleApproverChange(index, value ? [value] : [])}
                              onFilterChange={(filteredData) => {
                                setApproverDataFields((prev) => ({
                                  ...prev,
                                  approverData: filteredData,
                                }));
                              }}
                              onClose={() =>
                                setApproverDataFields((prev) => ({
                                  ...prev,
                                  approverData: originalApproverData,
                                  filterValue: "",
                                  search: "",
                                }))
                              }
                              filterable={true}
                              placeholder="Select approver name"
                            />
                            {validationErrors[`approver_${index}_approverName`] && (
                              <p className="error-msg">{validationErrors[`approver_${index}_approverName`]}</p>
                            )}
                          </div>

                          {/* Is Required Field */}
                          <div className="fullwidthelm" data-field={`approver_${index}_isRequired`}>
                            <label className="provider-elm">Is Required*</label>
                            <CustomDropdown
                              data={
                                filteredIsRequiredData[index] || [
                                  { text: "Yes", id: "yes" },
                                  { text: "No", id: "no" },
                                ]
                              }
                              dataList={[
                                { text: "Yes", id: "yes" },
                                { text: "No", id: "no" },
                              ]}
                              textField="text"
                              dataItemKey="id"
                              className="provider-elm"
                              valueData={
                                approver.isRequired
                                  ? { text: approver.isRequired, id: approver.isRequired.toLowerCase() }
                                  : null
                              }
                              onChange={(e) => {
                                updateApproverData(index, "isRequired", e?.text || "");
                                setFilteredIsRequiredData((prev) => ({
                                  ...prev,
                                  [index]: [
                                    { text: "Yes", id: "yes" },
                                    { text: "No", id: "no" },
                                  ],
                                }));
                              }}
                              onFilterChange={(filteredData) =>
                                setFilteredIsRequiredData((prev) => ({ ...prev, [index]: filteredData }))
                              }
                              onOpen={(data) => setFilteredIsRequiredData((prev) => ({ ...prev, [index]: data }))}
                              onClose={() =>
                                setFilteredIsRequiredData((prev) => ({
                                  ...prev,
                                  [index]: [
                                    { text: "Yes", id: "yes" },
                                    { text: "No", id: "no" },
                                  ],
                                }))
                              }
                              filterable={true}
                              autoClose={false}
                              autoBind={true}
                            />
                            {validationErrors[`approver_${index}_isRequired`] && (
                              <p className="error-msg">{validationErrors[`approver_${index}_isRequired`]}</p>
                            )}
                          </div>

                          <div className="approver-delete-section">
                            {index > 0 && (
                              <Tooltip position="bottom" anchorElement="target">
                                <button
                                  className="remove-btn-full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeApprover(index);
                                  }}
                                  title="Delete Approver"
                                >
                                  Delete Approver
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </ExpansionPanelContent>
                    )}
                  </Reveal>
                </ExpansionPanel>
              ))}

              {/* COMP_ADMIN Toggle Checkbox */}
              <div className="comp-admin-toggle">
                <span className="comp-admin-label">{`Include COMP_ADMIN as Level ${contractData.approvers.length + 1} Approver`}</span>
                <input
                  type="checkbox"
                  className="comp-admin-checkbox"
                  checked={includeCompAdmin}
                  onChange={(e) => setIncludeCompAdmin(e.target.checked)}
                />
              </div>

              {/* COMP_ADMIN ExpansionPanel - Only shown when checkbox is checked */}
              {includeCompAdmin && (
                <ExpansionPanel
                  title="Approver Details - COMP ADMIN"
                  expanded={expandedApprover === "comp_admin"}
                  tabIndex={0}
                  onAction={(event) => {
                    setExpandedApprover(event.expanded ? "" : "comp_admin");
                  }}
                >
                  <Reveal>
                    {expandedApprover === "comp_admin" && (
                      <ExpansionPanelContent>
                        <div className="approver-fields">
                          <div className="fullwidthelm">
                            <label className="provider-elm">Approver Level</label>
                            <input
                              type="text"
                              className="k-textbox provider-elm"
                              value={`Level ${contractData.approvers.length + 1}`}
                              disabled
                              style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                            />
                          </div>

                          <div className="fullwidthelm">
                            <label className="provider-elm">Approver Name</label>
                            <input
                              type="text"
                              className="k-textbox provider-elm"
                              value="COMP_ADMIN"
                              disabled
                              style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                            />
                          </div>

                          <div className="fullwidthelm">
                            <label className="provider-elm">Is Required</label>
                            <input
                              type="text"
                              className="k-textbox provider-elm"
                              value="No"
                              disabled
                              style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                            />
                          </div>
                        </div>
                      </ExpansionPanelContent>
                    )}
                  </Reveal>
                </ExpansionPanel>
              )}
            </div>

            {/* Documents */}
            {/* <div className="documents-section">
                            <h5 className="contract-details-header">DOCUMENTS</h5>
                            <div>
                                <ContractUploadFile
                                    openUploadPopup={openUploadPopup}
                                    setOpenUploadPopup={setOpenUploadPopup}
                                    onDocumentsUpdate={handleDocumentsUpdate}
                                />
                            </div>

                            {contractData.documents.map((doc, index) => (
                                <div key={`document_${index}`} className="document-item">
                                    <span className="document-name">{doc.name}</span>
                                    <span className="document-size">({Math.round(doc.size / 1024)} kb)</span>
                                    <button
                                        className="remove-btn"
                                        onClick={() => removeDocument(index)}
                                        title="Remove Document"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            ))}
                            <div className="document-item">
                                <i className="fas fa-file"></i>
                                <div className="document-info">
                                    <span className="document-name">Test_Document.docx</span>
                                    <span className="document-size">30 kb</span>
                                </div>
                                <button
                                    className="remove-btn"
                                    title="Remove Document"
                                >
                                    <i className="fas fa-times delete-doc"></i>
                                </button>
                            </div>
                        </div> */}
          </div>
        </div>
      </div>
    );
  };

  const headerConfig = {
    title: "Update Contract",
    id: "update-contract",
    buttons: {
      template_fx: () => (
        <GridButton
          onSubmit={handleSubmit}
          onCancel={handleBack}
          onBack={handleBack}
          isSubmitting={loadingState.submitting}
          hasChanges={hasChanges}
        />
      ),
    },
  };

  return (
    <div className="contract-management">
      <div className="content">
        <div className="content-head">
          <HeaderPage pageProps={pageProps} headerConfig={headerConfig} />
        </div>
        <div className="content-body" style={{ height: "100%" }}>
          {loadingState.initialLoad || loadingState.submitting ? (
            <LoadingPanel />
          ) : (
            <div className="Propos-container">
              <div className="att-main">
                {contractCategories.map((item) => {
                  return (
                    <div
                      className={`attr-col ${item.category_code == "org_attr" ? "org-attr-col" : "contract-attr-col"}`}
                      key={item.category_config_key}
                      draggable="true"
                      onDragStart={(e) => handleParentDragStart(e, item.category_config_key)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleParentDrop(e, item.category_config_key)}
                    >
                      <div id="header" className="attr-col-heading">
                        <i className="fas fa-arrows-alt"></i>
                        {item.category_label}
                      </div>
                      <div className="attr-col-content">
                        {item.category_code === "org_attr" && renderOrganizationalAttributes()}
                        {item.category_code === "contract_attr" && renderContractAttributes()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateContract;

export const GridButton = ({ onSubmit, onCancel, onBack, isSubmitting = false, hasChanges = false }) => {
  return (
    <>
      <Button type="button" className="cancelbtn" id="grid-incell-cancel" onClick={onCancel} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button
        type="submit"
        className="primarybtn"
        id="grid-incell-update"
        onClick={onSubmit}
        disabled={isSubmitting || !hasChanges}
      >
        {isSubmitting ? "Updating..." : "Update"}
      </Button>
      <Button
        title="Back to Contract Details"
        type="button"
        className="cancelbtn backbtn"
        id="grid-back-btn"
        onClick={onBack}
        disabled={isSubmitting}
      >
        <span className="k-button-text">
          <i className="fa fa-arrow-left"></i>
        </span>
      </Button>
    </>
  );
};
